package diana.soleil.design.iraniancanadienbazzar.feature.categories

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.WindowWidthClass
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.windowWidthClassFor
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppSearchBar
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.CategoryGridCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ErrorState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.SkeletonBox
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_business_count
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_cta_action
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_cta_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_cta_title
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_error
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_retry
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_search_empty
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_search_placeholder
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_subtitle
import diana.soleil.design.iraniancanadienbazzar.feature.categories.resources.categories_title
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * Stateful entry point: pulls [CategoriesViewModel] from Koin and renders the stateless screen.
 * Wired into the nav graph (`:shared`).
 */
@Composable
fun CategoriesRoute(
    onBack: () -> Unit,
    onCategoryClick: (categoryId: String) -> Unit,
    onAddBusiness: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: CategoriesViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    CategoriesScreen(
        state = state,
        onBack = onBack,
        onCategoryClick = onCategoryClick,
        onAddBusiness = onAddBusiness,
        onRetry = viewModel::retry,
        modifier = modifier,
    )
}

/**
 * Categories list — Stitch `f932f529` (Directory & Community System). Tokens + string resources only.
 * Stateless; driven by [CategoriesUiState] (categories come from `CategoryRepository` via the route).
 */
@Composable
fun CategoriesScreen(
    state: CategoriesUiState,
    onBack: () -> Unit,
    onCategoryClick: (categoryId: String) -> Unit,
    onAddBusiness: () -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    if (state.error) {
        ErrorState(
            message = stringResource(Res.string.categories_error),
            onRetry = onRetry,
            retryLabel = stringResource(Res.string.categories_retry),
            modifier = modifier,
        )
        return
    }
    BoxWithConstraints(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        // 2-up card grid on phones (owner direction 2026-07-11), scaling up on wider screens.
        val columns = when (windowWidthClassFor(maxWidth)) {
            WindowWidthClass.Compact -> 2
            WindowWidthClass.Medium -> 3
            WindowWidthClass.Expanded -> 4
        }
        var query by remember { mutableStateOf("") }
        val visibleCategories = if (query.isBlank()) {
            state.categories
        } else {
            state.categories.filter { it.displayName(AppTheme.language).contains(query.trim(), ignoreCase = true) }
        }
    LazyColumn(
        modifier = Modifier
            .fillMaxHeight()
            .widthIn(max = AppTheme.sizes.contentMaxWidth)
            .align(Alignment.TopCenter)
            .statusBarsPadding().navigationBarsPadding(),
        contentPadding = PaddingValues(
            start = AppTheme.spacing.lg,
            end = AppTheme.spacing.lg,
            top = AppTheme.spacing.md,
            bottom = AppTheme.spacing.xl,
        ),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        item {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.categories_back_a11y),
                onClick = onBack,
                tint = AppTheme.colors.onSurface,
            )
        }
        item {
            Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm)) {
                Text(
                    text = stringResource(Res.string.categories_title),
                    style = AppTheme.typography.headlineLg,
                    color = AppTheme.colors.primary,
                )
                Text(
                    text = stringResource(Res.string.categories_subtitle),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
            }
        }
        item {
            AppSearchBar(
                value = query,
                onValueChange = { query = it },
                placeholder = stringResource(Res.string.categories_search_placeholder),
                leading = { AppIcon("search", tint = AppTheme.colors.onSurfaceVariant) },
            )
        }
        if (state.loading) {
            // Shimmer grid mirroring the category cards.
            items(3) {
                Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                    repeat(columns) {
                        Box(modifier = Modifier.weight(1f)) {
                            SkeletonBox(
                                modifier = Modifier.fillMaxWidth()
                                    .height(AppTheme.sizes.resultImageHeight),
                            )
                        }
                    }
                }
            }
        }
        // Elevated rounded cards in a 2-up grid (3/4-up on wider screens); centered content.
        items(visibleCategories.chunked(columns), key = { it.first().id }) { rowItems ->
            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                rowItems.forEach { category ->
                    Box(modifier = Modifier.weight(1f)) {
                        CategoryGridCard(
                            name = category.displayName(AppTheme.language),
                            countLabel = stringResource(Res.string.categories_business_count, category.businessCount)
                                .takeIf { category.businessCount > 0 },
                            iconName = category.iconKey,
                            onClick = { onCategoryClick(category.id) },
                            imageRes = diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.categoryImageRes(category.id),
                        )
                    }
                }
                repeat(columns - rowItems.size) { Spacer(modifier = Modifier.weight(1f)) }
            }
        }
        if (!state.loading && query.isNotBlank() && visibleCategories.isEmpty()) {
            item {
                Text(
                    text = stringResource(Res.string.categories_search_empty, query.trim()),
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth().padding(vertical = AppTheme.spacing.lg),
                )
            }
        }
        if (query.isBlank()) {
            item { AddBusinessCta(onAddBusiness = onAddBusiness) }
        }
    }
    }
}

@Composable
private fun AddBusinessCta(onAddBusiness: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
            .padding(AppTheme.spacing.lg),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = stringResource(Res.string.categories_cta_title),
            style = AppTheme.typography.headlineMd,
            color = AppTheme.colors.onPrimary,
        )
        Text(
            text = stringResource(Res.string.categories_cta_subtitle),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onPrimary,
        )
        AppButton(
            text = stringResource(Res.string.categories_cta_action),
            onClick = onAddBusiness,
            variant = AppButtonVariant.Secondary,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}
